import { initializeApp } from 'firebase/app'
import { 
  getAuth, 
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  deleteUser
} from 'firebase/auth'
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  arrayUnion,
  arrayRemove,
  Timestamp,
  addDoc
} from 'firebase/firestore'
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage'
import { 
  getMessaging
} from 'firebase/messaging'
import { getFunctions, httpsCallable } from 'firebase/functions'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const app = initializeApp(firebaseConfig)

export const auth = getAuth(app)
const authPersistenceReady = setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error)
})
export const db = getFirestore(app)
export const storage = getStorage(app)
export const messaging = getMessaging(app)
export const functions = getFunctions(app, 'us-central1')

const releaseRemovedEmailCallable = httpsCallable(functions, 'releaseRemovedEmailForRegistration')

export const releaseRemovedEmailForRegistration = (email) =>
  releaseRemovedEmailCallable({ email })

// Google Auth Provider
export const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({
  prompt: 'select_account'
})

// ==================== AUTH FUNCTIONS ====================
export const signIn = async (email, password) => {
  await authPersistenceReady
  return signInWithEmailAndPassword(auth, email, password)
}

export const signUp = async (email, password) => {
  await authPersistenceReady
  return createUserWithEmailAndPassword(auth, email, password)
}

export const logOut = async () => {
  await authPersistenceReady
  return signOut(auth)
}

export const resetPassword = async (email) => {
  await authPersistenceReady
  return sendPasswordResetEmail(auth, email)
}

export const updateUserProfile = async (user, data) => {
  await authPersistenceReady
  return updateProfile(user, data)
}

export const verifyEmail = async (user) => {
  await authPersistenceReady
  return sendEmailVerification(user)
}

// Google Sign-In function
export const signInWithGoogle = async () => {
  await authPersistenceReady
  return signInWithPopup(auth, googleProvider)
}

export const deleteAuthUser = async (user) => {
  await authPersistenceReady
  return deleteUser(user)
}

// ==================== FIRESTORE FUNCTIONS ====================
export const createDocument = (collectionName, id, data) => 
  setDoc(doc(db, collectionName, id), data)

export const addDocument = (collectionName, data) => 
  addDoc(collection(db, collectionName), data)

export const getDocument = (collectionName, id) => 
  getDoc(doc(db, collectionName, id))

export const getDocuments = (collectionName) => 
  getDocs(collection(db, collectionName))

export const updateDocument = (collectionName, id, data) => 
  updateDoc(doc(db, collectionName, id), data)

export const deleteDocument = (collectionName, id) => 
  deleteDoc(doc(db, collectionName, id))

export const queryDocuments = async (collectionName, conditions) => {
  let q = collection(db, collectionName)
  conditions.forEach(condition => {
    q = query(q, where(condition.field, condition.operator, condition.value))
  })
  return getDocs(q)
}

export const queryDocumentsAdvanced = async (collectionName, options = {}) => {
  let q = collection(db, collectionName)
  
  if (options.whereConditions) {
    options.whereConditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator, condition.value))
    })
  }
  
  if (options.orderBy) {
    options.orderBy.forEach(order => {
      q = query(q, orderBy(order.field, order.direction || 'asc'))
    })
  }
  
  if (options.limit) {
    q = query(q, limit(options.limit))
  }
  
  return getDocs(q)
}

// ==================== STORAGE FUNCTIONS ====================
export const uploadImage = async (path, file) => {
  const storageRef = ref(storage, path)
  await uploadBytes(storageRef, file)
  return await getDownloadURL(storageRef)
}

export const deleteImage = async (path) => {
  const storageRef = ref(storage, path)
  await deleteObject(storageRef)
}

export const uploadMultipleImages = async (pathsAndFiles) => {
  const uploadPromises = pathsAndFiles.map(async ({ path, file }) => {
    const url = await uploadImage(path, file)
    return { path, url }
  })
  return Promise.all(uploadPromises)
}

// ==================== EXPORTS ====================
export { Timestamp, arrayUnion, arrayRemove }
export default app